import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { DocumentTile } from "../ProjectDocuments";
import { Collapse } from "@/components/design/Collapse";
import { Button } from "@/components/design/Button";
import { ProfileSilhouette } from "@/components/design/ProfileSilhouette";
import { Switch } from "@/components/design/Switch";
import { dataToDesign } from "@/components/match/match-design-mapping";
import { DEFAULT_TEAM_PROFILE_ID, EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import { newCondition } from "@/components/match/design-codec";
import { describesRole } from "@/components/match/match-mode";
import type { MatchMode as MatchModeToken } from "@/components/match/match-mode";
import type { MatchData } from "@/components/match/match-design-types";
import {
  MatchSkillsDiagram,
  type MatchSkillEntry,
} from "@/components/match/requirements/MatchSkillsDiagram";
import { SubsectionHeader } from "@/components/match/requirements/row-primitives";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { toStoragePercent } from "@/lib/skill-levels";
import { userErrorMessage } from "@/lib/user-error";
import {
  AttachProjectDocumentDocument,
  BulkAddCandidatesDocument,
  DocumentContentDocument,
  CreateMatchDocument,
  DeleteMatchDocument,
  LinkProjectMatchDocument,
  MatchMode,
} from "@/graphql/generated/operations";
import type { PlanPerson, ProjectRequirements } from "../project-plan";

// Der Schritt zwischen Projekt und Match. Vorher führte hier ein blosser Link
// nach MATCH, und was davon mitging, erfuhr man erst drüben - nämlich nichts.
//
// Was hier steht, ist deshalb kein Formular, sondern eine Liste von Zusagen:
// Das nehme ich mit, das lasse ich hier. Abwählbar, weil ein Projekt mehrere
// Matches haben darf und das zweite selten dieselben Anforderungen braucht.
//
// GEZEIGT statt behauptet: die Fachwissen-Leiter und die Powers stehen hier so,
// wie sie im Anforderungen-Reiter stehen. Ein Wort wie "Gewichtung übernehmen"
// ist keine Zusage, die man prüfen kann - eine Silhouette, auf der ein Balken
// oben und einer unten steht, schon.
//
// Die Übernahme ist eine KOPIE, kein lebender Link. Zwei bearbeitbare Quellen
// für dieselbe Anforderung sind genau die Drift, gegen die die Projekt-Entität
// gebaut ist. Verbunden bleiben die beiden trotzdem: die Relation project_match
// hält fest, dass dieses Match zu diesem Projekt gehört, und im Projekt steht
// danach ein Verweis darauf.

type Take = { description: boolean; requirements: boolean; scorecard: boolean; people: boolean; documents: boolean };

// Die drei Modi, die Menschen suchen (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4).
// RANK_COMPANIES fehlt bewusst: der Unternehmens-Arm sucht Unternehmen statt Personen
// und liest weder Team-Profil noch die Fachwissen-Leiter, die dieses Projekt
// mitgibt - die Karte hier anzubieten hiesse, eine Übernahme zu versprechen, die
// drüben nichts vorfindet.
type PickableMode = Extract<MatchModeToken, "OPTIMIZE" | "ASSESS" | "RANK">;

// Dieselben Texte wie im Anlegen-Dialog (components/match/ModeSelect): wer den
// Modus einmal in MATCH gewählt hat, soll hier nicht dieselbe Entscheidung unter
// anderen Worten wiederfinden.
const MODES: Array<{
  mode: PickableMode;
  title: { id: string; defaultMessage: string };
  slogan: { id: string; defaultMessage: string };
  output: { id: string; defaultMessage: string };
}> = [
  {
    mode: "OPTIMIZE",
    title: { id: "match.modePicker.optimizeTitle", defaultMessage: "Team optimieren" },
    slogan: {
      id: "match.modePicker.optimizeSlogan",
      defaultMessage:
        "Du hast einen Pool an Personen und willst das beste Team für ein Projekt oder eine Aufgabe zusammenstellen.",
    },
    output: { id: "match.modePicker.optimizeOutput", defaultMessage: "ein Team" },
  },
  {
    mode: "ASSESS",
    title: { id: "match.modePicker.assessTitle", defaultMessage: "Team bewerten" },
    slogan: {
      id: "match.modePicker.assessSlogan",
      defaultMessage:
        "Du hast bereits ein Team im Kopf und willst sehen, wie gut es passt und wo die Lücken sind.",
    },
    output: { id: "match.modePicker.assessOutput", defaultMessage: "Einschätzung + Lücken" },
  },
  {
    mode: "RANK",
    title: { id: "match.modePicker.rankTitle", defaultMessage: "Kandidaten vergleichen" },
    slogan: {
      id: "match.modePicker.rankSlogan",
      defaultMessage:
        "Du besetzt eine offene Stelle und willst Kandidaten danach sortieren, wer am besten passt.",
    },
    output: { id: "match.modePicker.rankOutput", defaultMessage: "Rangliste" },
  },
];

// Vom Wort-Token auf den GraphQL-Enum. Absichtlich eine vollständige Tabelle und
// kein Cast: kommt ein vierter Modus dazu, bricht hier der Build, statt dass ein
// unbekannter String bis zur Mutation durchrutscht.
const MODE_ENUM: Record<PickableMode, MatchMode> = {
  OPTIMIZE: MatchMode.Optimize,
  ASSESS: MatchMode.Assess,
  RANK: MatchMode.Rank,
};

export function ProjectToMatchScreen({
  projectId,
  title,
  description,
  requirements,
  people,
  documents,
}: {
  projectId: string | null;
  title: string;
  description: string;
  requirements: ProjectRequirements;
  /** Wer schon im Projekt ist: Mitglieder mit Zugriff und Gefragte, in dieser Reihenfolge. */
  people: readonly PlanPerson[];
  /** Die Dokumente des Projekts, als Verweise auf die eigene Bibliothek. */
  documents: readonly { id: string; name: string; size?: number }[];
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const validationFallback = useValidationFallback();
  const [createMatch, { loading: creating }] = useMutation(CreateMatchDocument);
  const [linkMatch] = useMutation(LinkProjectMatchDocument);
  const [addCandidates] = useMutation(BulkAddCandidatesDocument);
  const [attachDocument] = useMutation(AttachProjectDocumentDocument);
  const [deleteMatch] = useMutation(DeleteMatchDocument);
  const apollo = useApolloClient();
  const [error, setError] = useState<string | null>(null);
  /** Ein angelegtes und verknüpftes Match, in das nicht alles übernommen werden konnte. */
  const [partial, setPartial] = useState<{ matchId: string; missing: "people" | "documents" | "both" } | null>(null);
  const [take, setTake] = useState<Take>({
    description: true,
    requirements: true,
    scorecard: true,
    people: true,
    documents: true,
  });
  // Abgewählt statt ausgewählt: die Namen der Gefragten lädt `useProjectPeople`
  // nach, die Liste ist beim ersten Render also noch nicht vollständig. Wer
  // später dazukommt, ist so trotzdem dabei, ohne dass ein Effekt den Zustand
  // nachträglich korrigieren muss.
  const [dropped, setDropped] = useState<readonly string[]>([]);
  const [droppedDocuments, setDroppedDocuments] = useState<readonly string[]>([]);
  // Vorausgewählt ist Optimieren, weil ein Projekt Menschen für seine Aufgaben
  // sucht. Vorher war es die einzige Möglichkeit, ohne dass jemand gefragt wurde.
  const [mode, setMode] = useState<PickableMode>("OPTIMIZE");

  const takenPeople = people.filter((p) => !dropped.includes(p.id));
  const takenDocuments = documents.filter((d) => !droppedDocuments.includes(d.id));
  const expertiseCount = requirements.expertise.length;
  const hasWeights = Object.keys(requirements.elementWeights).length > 0;
  // Rank besetzt EINE Stelle, dort ist "mindestens drei Personen können das"
  // bedeutungslos. Die Zahl verschwindet deshalb sichtbar aus der Vorschau,
  // sobald der Modus gewechselt wird - und genau so wird sie auch geschrieben.
  const carriesPersonCount = !describesRole(mode);

  const entries: MatchSkillEntry[] = requirements.expertise.map((r) => ({
    id: r.id,
    description: r.description,
    level: r.level,
    requirement: r.requirement,
    ...(carriesPersonCount && r.personCount !== undefined ? { personCount: r.personCount } : {}),
  }));

  async function go() {
    setError(null);
    let matchId: string;
    try {
      // Die Fachwissen-Stufe wird im Match als Prozent geführt, nicht als Stufe
      // 1..7 (ExpertiseLevel.toStoragePercent). Wer das vergisst, legt ein Match
      // mit Stufe "7 Prozent" an.
      const conditions = take.requirements
        ? requirements.expertise.map((r) =>
            newCondition("ExpertiseCondition", {
              expertise: r.description,
              level: r.level === 0 ? 0 : toStoragePercent(r.level),
              requirement: r.requirement,
              aiMode: "NO_AI",
              // genesis lehnt ein OPTIMIZE-Match ab, dessen Fachwissen-Bedingung
              // nicht "mindestens Stufe X bei mindestens N Personen" heisst:
              // operator GE und composition MIN sind Pflicht, nicht Geschmack.
              operator: "GE",
              // Rank kennt keine Personenzahl, dort bleibt es bei der Vorgabe
              // aus newCondition ({operator: "ALL", value: null}) - dieselbe
              // Fallunterscheidung wie in ExpertiseSection.
              ...(carriesPersonCount
                ? { composition: { operator: "MIN" as const, value: r.personCount ?? 1 } }
                : {}),
            }),
          )
        : [];

      const data: MatchData = {
        ...EMPTY_MATCH_DATA,
        matchTitle: title,
        ...(take.description ? { projectName: title, projectDescription: description } : {}),
        ...(take.scorecard && hasWeights
          ? {
              teamProfile: {
                id: EMPTY_MATCH_DATA.teamProfile?.id ?? DEFAULT_TEAM_PROFILE_ID,
                name: EMPTY_MATCH_DATA.teamProfile?.name ?? "",
                elementWeights: requirements.elementWeights,
              },
            }
          : {}),
        conditions,
      };

      const result = await createMatch({
        variables: {
          input: {
            data: {
              matchMode: MODE_ENUM[mode],
              name: title || null,
              description: take.description ? description || null : null,
              design: dataToDesign(data),
            },
          },
        },
      });
      const payload = result.data?.createMatch;
      throwIfValidationRejected(payload, "ValidateMatchResult", validationFallback);
      if (payload?.__typename !== "CreateMatchResult") return;
      matchId = payload.result.id;
    } catch (e) {
      setError(userErrorMessage(intl, e));
      return;
    }

    // Verknüpfen direkt nach dem Anlegen, vor dem Befüllen: scheitert danach etwas, ist das Match
    // unvollständig, aber nie eine Waise, von der das Projekt nichts weiß (spark#1001). Scheitert
    // das Verknüpfen selbst, ist es noch löschbar, und kein Match ist besser als ein verwaistes.
    if (projectId) {
      try {
        await linkMatch({ variables: { input: { projectId, matchId } } });
      } catch (e) {
        const removed = await deleteMatch({ variables: { input: { id: matchId } } }).then(
          () => true,
          () => false,
        );
        setError(
          removed
            ? userErrorMessage(intl, e)
            : intl.formatMessage({
                id: "project.toMatch.orphaned",
                defaultMessage:
                  "Das Match ist angelegt, ließ sich aber nicht mit dem Projekt verknüpfen. Du findest es unter MATCH.",
              }),
        );
        return;
      }
    }

    // Befüllen: Personen und Dokumente sind unabhängig voneinander, ein Fehlschlag beim einen hält
    // das andere nicht auf. Was fehlt, wird genannt und lässt sich im Match nachtragen.
    let peopleFailed = false;
    let documentsFailed = false;

    // Die Menschen sind das einzige hier, was keine Kopie im Dokument ist:
    // ein Kandidat ist eine eigene Zeile (match_candidate) und braucht das
    // Match, also geht er erst nach dem Anlegen hin. Dieselbe Mutation, die
    // auch der Match-Editor für "Personen hinzufügen" benutzt.
    if (take.people && takenPeople.length > 0) {
      try {
        await addCandidates({
          variables: {
            input: {
              match: { id: matchId },
              data: takenPeople.map((person) => ({ person: { id: person.id }, locked: false })),
            },
          },
        });
      } catch {
        peopleFailed = true;
      }
    }

    // Dokumente gehen als KOPIE mit, nicht als Verweis: das Match führt seine
    // Dateien in `design.project.documents` mit eigener backdrop-Id, und
    // `attachProjectDocument` nimmt nur rohen Inhalt entgegen. Also wird je
    // Dokument der Inhalt geholt und angehängt. Ein Verweis wäre billiger,
    // bräuchte aber ein Feld in genesis, das es nicht gibt - und er würde der
    // Zusage widersprechen, dass Änderungen im Match das Projekt nicht
    // berühren.
    if (take.documents && takenDocuments.length > 0) {
      try {
        const files = (
          await Promise.all(
            takenDocuments.map(async (doc) => {
              const { data } = await apollo.query({
                query: DocumentContentDocument,
                variables: { id: doc.id },
                fetchPolicy: "network-only",
              });
              const file = data?.document?.content;
              if (!file?.content) return null;
              return {
                fileName: data?.document?.fileName?.trim() || doc.name,
                mimeType: file.mimeType ?? "application/octet-stream",
                content: file.content,
              };
            }),
          )
        ).flatMap((f) => (f ? [f] : []));
        if (files.length > 0) {
          await attachDocument({ variables: { input: { matchId, files } } });
        }
      } catch {
        documentsFailed = true;
      }
    }

    if (peopleFailed || documentsFailed) {
      setPartial({ matchId, missing: peopleFailed && documentsFailed ? "both" : peopleFailed ? "people" : "documents" });
      return;
    }
    navigate(`/match/${matchId}`);
  }

  const rows: { key: keyof Take; label: string; detail: string; disabled?: boolean }[] = [
    {
      key: "description",
      label: intl.formatMessage({ id: "project.toMatch.description", defaultMessage: "Beschreibung" }),
      detail: description
        ? description.slice(0, 90)
        : intl.formatMessage({ id: "project.toMatch.nothingYet", defaultMessage: "Noch nichts hinterlegt" }),
      disabled: !description,
    },
    {
      key: "requirements",
      label: intl.formatMessage({ id: "project.toMatch.requirements", defaultMessage: "Anforderungen" }),
      detail: intl.formatMessage(
        {
          id: "project.toMatch.requirementsDetail",
          defaultMessage:
            "{count, plural, =0 {Noch nichts hinterlegt} one {Ein Fachwissen} other {# Fachwissen}}",
        },
        { count: expertiseCount },
      ),
      disabled: expertiseCount === 0,
    },
    {
      key: "scorecard",
      label: intl.formatMessage({ id: "project.toMatch.scorecard", defaultMessage: "Power-Scorecard" }),
      detail: hasWeights
        ? intl.formatMessage({ id: "project.toMatch.scorecardSet", defaultMessage: "Gewichtung übernehmen" })
        : intl.formatMessage({ id: "project.toMatch.nothingYet", defaultMessage: "Noch nichts hinterlegt" }),
      disabled: !hasWeights,
    },
    {
      key: "documents",
      label: intl.formatMessage({ id: "project.toMatch.documents", defaultMessage: "Dokumente" }),
      detail: intl.formatMessage(
        {
          id: "project.toMatch.documentsDetail",
          defaultMessage:
            "{count, plural, =0 {Noch nichts hinterlegt} one {Ein Dokument} other {# Dokumente}}",
        },
        { count: takenDocuments.length },
      ),
      disabled: documents.length === 0,
    },
    {
      key: "people",
      label: intl.formatMessage({ id: "project.toMatch.people", defaultMessage: "Personen" }),
      detail: intl.formatMessage(
        {
          id: "project.toMatch.peopleDetail",
          defaultMessage:
            "{count, plural, =0 {Noch niemand im Projekt} one {Eine Person aus dem Projekt} other {# Personen aus dem Projekt}}",
        },
        { count: takenPeople.length },
      ),
      disabled: people.length === 0,
    },
  ];

  // Abgewählt heisst weg, nicht blass. Eine gedimmte Fachwissen-Leiter blieb
  // eine Fachwissen-Leiter: sie nahm denselben Platz ein, und die Liste sah
  // gleich lang aus, egal wie viel man abgewählt hatte. Eingeklappt wird die
  // Entscheidung sofort sichtbar, und was übrig bleibt, ist genau das, was
  // mitgeht. Der Schalter daneben sagt weiterhin, dass es die Zeile noch gibt.
  function preview(active: boolean, children: ReactNode) {
    return (
      <Collapse open={active}>
        <div className="mt-3">{children}</div>
      </Collapse>
    );
  }

  return (
    <div className="max-w-3xl">
      <p className="smycles-field-meta max-w-[62ch] text-ink">
        <FormattedMessage
          id="project.toMatch.sub"
          defaultMessage="Das Match startet nicht bei null. Wähle, was aus dem Projekt mitgeht."
        />
      </p>

      <div className="mt-5">
        <SubsectionHeader
          title={intl.formatMessage({
            id: "match.modePicker.title",
            defaultMessage: "Welche Frage soll dieses Match beantworten?",
          })}
        />
        <div
          className="mt-1 grid gap-3 @sm:grid-cols-3"
          role="radiogroup"
          aria-label={intl.formatMessage({ id: "match.modePicker.title" })}
        >
          {MODES.map((m) => {
            const selected = m.mode === mode;
            return (
              <button
                key={m.mode}
                id={`project-to-match-mode-${m.mode.toLowerCase()}`}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setMode(m.mode)}
                className={`box-flat bg-page flex flex-col gap-2 p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${
                  selected ? "smycles-selected ring-2 ring-accent-decoration" : "hover:ring-1 hover:ring-ink-ui"
                }`}
              >
                <span className="font-serif text-smycles-title leading-none">
                  <FormattedMessage {...m.title} />
                </span>
                <span className="text-smycles-meta leading-snug text-ink">
                  <FormattedMessage {...m.slogan} />
                </span>
                <span className="smycles-field-label mt-auto pt-1">
                  <FormattedMessage id="match.modePicker.outputLabel" defaultMessage="Ergebnis" />
                  {": "}
                  <FormattedMessage {...m.output} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <SubsectionHeader
          title={intl.formatMessage({
            id: "project.toMatch.takeTitle",
            defaultMessage: "Was mitgeht",
          })}
        />
        <div className="mt-1 box bg-surface p-4">
          <ul className="divide-y divide-ink/10">
            {rows.map((row) => {
              const active = take[row.key] && !row.disabled;
              return (
                <li key={row.key} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className={`smycles-field-value ${row.disabled ? "text-ink-muted" : ""}`}>{row.label}</div>
                      <div className="smycles-field-meta mt-0.5 truncate text-ink-muted">{row.detail}</div>
                    </div>
                    <Switch
                      checked={active}
                      disabled={row.disabled}
                      onChange={(next) => setTake((cur) => ({ ...cur, [row.key]: next }))}
                      ariaLabel={row.label}
                    />
                  </div>

                  {/* Die Leiter ist dieselbe wie im Anforderungen-Reiter, nur
                      unbedienbar: Stufen, Muss-Schloss und Personenzahl liest man
                      hier genauso ab, geändert wird drüben. */}
                  {row.key === "requirements" && expertiseCount > 0
                    ? preview(
                        active,
                        <MatchSkillsDiagram
                          entries={entries}
                          readOnly
                          personCount={carriesPersonCount}
                          onToggleRequirement={() => {}}
                          onAdd={() => {}}
                          onRename={() => {}}
                          onDelete={() => {}}
                        />,
                      )
                    : null}

                  {/* Die Silhouette statt des Editors: hier wird nichts gewichtet,
                      hier wird gezeigt, wie gewichtet wurde. Ohne Klick-Handler
                      rendert der Baustein als reine Anzeige. */}
                  {row.key === "scorecard" && hasWeights
                    ? preview(
                        active,
                        <ProfileSilhouette elementWeights={requirements.elementWeights} className="px-1" />,
                      )
                    : null}

                  {/* Dieselben Kacheln wie im Dokumente-Reiter, dasselbe Raster:
                      ein Dokument sieht überall gleich aus, und das ✕ nimmt es
                      aus der Übernahme - nicht aus dem Projekt. */}
                  {row.key === "documents" && documents.length > 0
                    ? preview(
                        active,
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,9rem))] gap-3">
                          {documents.map((doc) => (
                            <DocumentTile
                              key={doc.id}
                              id={`project-to-match-document-${doc.id}`}
                              name={doc.name}
                              size={doc.size}
                              dropped={droppedDocuments.includes(doc.id)}
                              onRemove={() =>
                                setDroppedDocuments((cur) =>
                                  cur.includes(doc.id) ? cur.filter((id) => id !== doc.id) : [...cur, doc.id],
                                )
                              }
                            />
                          ))}
                        </div>,
                      )
                    : null}

                  {/* Einzeln abwählbar, nicht nur als Ganzes: das zweite Match
                      eines Projekts sucht selten für dieselben Leute, und wer
                      hier alle mitnimmt, muss sie drüben wieder aussortieren.
                      Dieselbe Zeile wie in der Community-Auswahl und im
                      Team-Reiter, damit derselbe Mensch überall gleich aussieht. */}
                  {row.key === "people" && people.length > 0
                    ? preview(
                        active,
                        <div className="space-y-2">
                          {people.map((person) => {
                            const taken = !dropped.includes(person.id);
                            return (
                              <PersonResultRow
                                key={person.id}
                                person={person}
                                title={person.name}
                                sub=""
                                embedded
                                actions={
                                  <Button
                                    id={`project-to-match-person-${person.id}`}
                                    variant={taken ? "primary" : "secondary"}
                                    size="sm"
                                    disabled={!active}
                                    onClick={() =>
                                      setDropped((cur) =>
                                        taken ? [...cur, person.id] : cur.filter((id) => id !== person.id),
                                      )
                                    }
                                  >
                                    {taken ? (
                                      <FormattedMessage id="project.toMatch.peopleTaken" defaultMessage="Geht mit" />
                                    ) : (
                                      <FormattedMessage
                                        id="project.toMatch.peopleDropped"
                                        defaultMessage="Bleibt hier"
                                      />
                                    )}
                                  </Button>
                                }
                              />
                            );
                          })}
                        </div>,
                      )
                    : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <p className="smycles-field-meta mt-3 max-w-[62ch] text-ink-muted">
        <FormattedMessage
          id="project.toMatch.copyNote"
          defaultMessage="Übernommen wird eine Kopie: was du im Match änderst, ändert das Projekt nicht. Verbunden bleiben beide trotzdem, das Match erscheint danach im Projekt."
        />
      </p>

      {error ? <p id="project-to-match-error" className="mt-3 smycles-field-meta text-ink">{error}</p> : null}

      {partial ? (
        <div className="mt-5">
          <p id="project-to-match-partial" role="alert" className="smycles-field-meta text-ink">
            <FormattedMessage
              id="project.toMatch.partial"
              defaultMessage="Das Match ist angelegt und mit dem Projekt verknüpft, aber {missing, select, people {die Personen} documents {die Dokumente} other {die Personen und die Dokumente}} ließen sich nicht übernehmen. Du kannst sie im Match nachtragen."
              values={{ missing: partial.missing }}
            />
          </p>
          <Button id="project-to-match-openpartial" className="mt-3" variant="primary" onClick={() => navigate(`/match/${partial.matchId}`)}>
            <FormattedMessage id="project.toMatch.openPartial" defaultMessage="Match öffnen" />
          </Button>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <Button id="project-to-match-go" variant="primary" onClick={go} disabled={creating || !projectId}>
            {creating ? (
              <FormattedMessage id="project.toMatch.creating" defaultMessage="Match wird angelegt …" />
            ) : (
              <FormattedMessage id="project.toMatch.go" defaultMessage="Match anlegen und öffnen" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
